import { defineMessage } from "@lingui/macro";
import { graphql as gatsbyGraphQL } from "~/gql";
import type {
  FilterDefinitions,
  Aggregation,
} from "@bottlebooks/bottlebooks-site-base/src/components/Filters/useIndex";
import { rankings } from "match-sorter";
import useSiteConfig from "../useSiteConfig";

export const fragment = gatsbyGraphQL(/* GraphQL */ `
  # We won't be able to support multi-events until they are consolidated on the GraphQL
  # fragment exhibitorMultiEventFilter on Exhibitor {
  #   event {
  #     eventId
  #     name
  #   }
  # }

  fragment exhibitorFilters on SingleRegistration {
    companyId
    stand {
      name
      roomName
    }
    registrationStatus
    registeredProducts {
      totalCount
    }
    characteristicsSummary
    regionSummary
    priceSummary
    priceRangeSummary
    primaryVarietySummary
    productTypeSummary
    profile {
      name
      sortName
      countryName: country(format: LOCALIZED)
      region
      city
      distributionInCountries
      seekingDistributionInCountries
      # priceRanges
      sortName
      sustainabilityCertifications {
        name
      }
    }
  }
  fragment exhibitorFilters_RegisteredBrand on RegisteredBrand {
    brandId
    profile {
      distributionInCountries
      seekingDistributionInCountries
    }
    isSeekingDistribution
  }
`);

// TODO generate this automatically from the fragment.
export interface Exhibitor {
  event?: { name?: string } | null;
  region?: string | null;
  characteristicsSummary?: (string | null)[] | null;
  city?: string | null;
  countryName?: string | null;
  isSeekingDistribution: boolean | null;
  priceRangeSummary?: (string | null)[] | null;
  productTypeSummary?: (string | null)[] | null;
  profile?: {
    distributionInCountries?: (string | null)[] | null;
    seekingDistributionInCountries?: (string | null)[] | null;
    sustainabilityCertifications?:
      | {
          name?: string | null;
        }[]
      | null;
  } | null;
  primaryVarietySummary?: (string | null)[] | null;
  regionSummary?: (string | null)[] | null;
  stand?: {
    name?: string | null;
    roomName: string | null;
  } | null;
}

export default function useExhibitorFilters(): FilterDefinitions<Exhibitor> {
  const { showDistributionDetails } = useSiteConfig();
  return {
    aggregations: {
      event: {
        title: defineMessage({ message: "Events" }),
        // multiSelect: false, // TODO Implement
        size: 20,
        get: (exhibitor) => exhibitor.event?.name,
      },
      location: {
        title: defineMessage({ message: "Locations" }),
        // multiSelect: false, // TODO Implement
        size: 20,
        get: (exhibitor) => exhibitor.stand?.roomName || [],
      },
      highlights: {
        title: defineMessage({ message: "Highlights" }),
        // multiSelect: false, // TODO Implement
        size: 20,
        get: "offeringTypes",
        translations: {
          videos: defineMessage({ message: "Videos" }),
          onlineShop: defineMessage({ message: "Online shop" }),
          imageGallery: defineMessage({ message: "Image gallery" }),
        },
      },
      regionHierarchy: {
        title: defineMessage({ message: "Winegrowing regions" }),
        conjunction: false,
        // multiSelect: false,
        size: 100,
        get: (exhibitor) => {
          return exhibitor?.regionSummary || [];
        }, // Excludes nulls
      },
      sustainabilityCertifications: {
        title: defineMessage({ message: "Certifications" }),
        conjunction: false,
        size: 100,
        get: (exhibitor) => {
          const certifications =
            exhibitor.profile?.sustainabilityCertifications || [];
          const certNames = certifications
            .map((cert) => cert.name)
            .filter(Boolean);
          if (!certNames.length) return [];
          return ["Any", ...certNames];
        }, // Excludes nulls
      },
      productionMethods: {
        title: defineMessage({ message: "Production methods" }),
        conjunction: false,
        size: 20,
        get: (exhibitor) =>
          exhibitor?.characteristicsSummary
            ?.filter((value) =>
              [
                "VEGAN",
                "ORGANIC",
                "VEGETARIAN",
                "BIODYNAMIC",
                "KOSHER",
                "SUSTAINABLE",
              ].includes(value || "")
            )
            .map((e) => e || "") || [],
        translations: {
          VEGAN: defineMessage({ message: "vegan" }),
          ORGANIC: defineMessage({ message: "organic" }),
          VEGETARIAN: defineMessage({ message: "vegetarian" }),
          BIODYNAMIC: defineMessage({ message: "biodynamic" }),
          KOSHER: defineMessage({ message: "kosher" }),
          SUSTAINABLE: defineMessage({ message: "sustainable" }),
        },
      },
      priceRanges: {
        title: defineMessage({ message: "Price ranges" }),
        conjunction: false,
        // multiSelect: false,
        size: 100,
        get: (exhibitor) => {
          return exhibitor?.priceRangeSummary || [];
        }, // Excludes nulls
      },
      productType: {
        title: defineMessage({ message: "Product types" }),
        conjunction: false,
        get: (exhibitor: Exhibitor) => {
          return exhibitor?.productTypeSummary?.filter(Boolean) || [];
        },
        translations: {
          WINE: defineMessage({ message: "Other wine" }),
          SPIRIT: defineMessage({ message: "Spirit" }),
          Stillwein: defineMessage({ message: "Wine" }),
          BEER: defineMessage({ message: "Beer and more" }),
        },
      },
      // This needs to be customized for each event that needs it.
      // calculatedPriceRanges: {
      //   title: defineMessage({ message: 'Price ranges' }),
      //   conjunction: false,
      //   size: 50,
      //   sort: 'term',
      //   order: 'asc',
      //   // This comes from ProductsTemplate.afterQuery.
      //   get: (exhibitor) => {
      //     const prices = (exhibitor?.priceSummary || []).map((price) => {
      //       if (price < 15) return '$';
      //       if (price < 25) return '$$';
      //       if (price < 45) return '$$$';
      //       if (price < 85) return '$$$$';
      //       return '$$$$$';
      //     });
      //     // Remove duplicates
      //     return [...new Set(prices || [])];
      //   }, // Excludes nulls
      //   translations: {
      //     $: defineMessage({ message: 'Less than $15' }),
      //     $$: defineMessage({ message: '$15-25' }),
      //     $$$: defineMessage({ message: '$25-45' }),
      //     $$$$: defineMessage({ message: '$45-85' }),
      //     $$$$$: defineMessage({ message: 'Greater than $85' }),
      //   },
      // },
      primaryGrapeVariety: {
        title: defineMessage({ message: "Primary grape varieties" }),
        conjunction: false,
        size: 300,
        get: (exhibitor) => {
          return exhibitor?.primaryVarietySummary || [];
        },
      },
      countryName: {
        title: defineMessage({ message: "Countries" }),
        size: 100,
        conjunction: false,
        // Remove null values
        get: (exhibitor) => exhibitor.countryName || [],
      },
      ...getDistributionFilters(showDistributionDetails),
    },

    sortings: {
      byStandName: {
        title: defineMessage({ message: "Table number" }),
        field: [sortBy.standName, "profile.sortName", "name"],
        group: (exhibitor) => exhibitor.stand?.roomName,
        groupHeader: "room",
        order: "asc",
      },
      // byDataCompleteness: {
      //   title: defineMessage({ message: 'By data completeness' }),
      //   field: [
      //     sortBy.standName,
      //     sortBy.dataCompleteness,
      //     'profile.sortName',
      //     'name',
      //   ],
      //   order: 'asc',
      // },
      byExhibitorName: {
        title: defineMessage({ message: "Exhibitor name" }),
        field: ["profile.sortName", "name"],
        order: "asc",
      },
    },
    search: {
      threshold: rankings.WORD_STARTS_WITH,
      keys: [
        { key: "name", threshold: rankings.CONTAINS },
        (exhibitor) =>
          `${exhibitor.countryName || ""} ${exhibitor.region || ""} ${
            exhibitor.city || ""
          }`.trim(),
        (exhibitor) => exhibitor?.stand?.name?.replace(/[()]/g, "") || "",
      ],
    },
  };
}

function getDistributionFilters(
  showDistributionDetails: "inMarket" | "global" | "all" | "none"
): Record<string, Aggregation<Exhibitor>> {
  switch (showDistributionDetails) {
    case "inMarket":
      return { isSeekingDistribution };

    case "global":
      return {
        distributionInCountries,
        seekingDistributionInCountries,
      };

    case "all":
      return {
        isSeekingDistribution,
        distributionInCountries,
        seekingDistributionInCountries,
      };

    case "none":
    default:
      return {};
  }
}

const distributionInCountries: Aggregation<Exhibitor> = {
  title: defineMessage({ message: "Distribution in countries" }),
  size: 200,
  conjunction: true,
  multiSelect: true,
  get: (exhibitor) =>
    (exhibitor.profile?.distributionInCountries?.filter(Boolean) as string[]) ||
    [],
};

const seekingDistributionInCountries: Aggregation<Exhibitor> = {
  title: defineMessage({ message: "Seeking distribution" }),
  size: 200,
  conjunction: true,
  sort: "term",
  multiSelect: true,
  get: (exhibitor) =>
    (exhibitor.profile?.seekingDistributionInCountries?.filter(
      Boolean
    ) as string[]) || [],
};

const isSeekingDistribution: Aggregation<Exhibitor> = {
  title: defineMessage({ message: "Distribution in Market" }),
  size: 100,
  conjunction: false,
  sort: "term",
  get: (exhibitor) => String(exhibitor.isSeekingDistribution || false),
  translations: {
    true: defineMessage({ message: "Seeking Distribution" }),
    false: defineMessage({ message: "Has Distribution" }),
  },
};

const completeness = {
  ORGANIZER_REVIEWED: 1,
  STEP_COMPLETED: 2,
  STEP_PAYMENT: 2,
  STEP_REVIEW: 3,
};

// Sort helper functions
const sortBy = {
  standName: (exhibitor: Exhibitor) => {
    return (
      (exhibitor?.stand?.name
        ?.split(/[\s,-]+/)
        .map((segment) =>
          segment === "Tisch"
            ? undefined
            : Number(segment)
            ? segment.padStart(3, "0")
            : segment
        )
        .filter(Boolean) as string[]) || []
    );
  },
  dataCompleteness: (exhibitor) =>
    completeness[exhibitor?.registrationStatus] -
    (exhibitor?.registeredProducts?.totalCount > 0 ? 1 : 0),
};
